import React, {Component, Fragment} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import {getSortedRowsBy} from '../../../../utils';
import {
  closeActionsView, getFsEntityListFromAction, getActions, getChildrenOfAction, showActionProgress,
  showScheduledPauseModal, showActionSubscriberView
} from '../../actions';
import ProblemDashboard from './ProblemDashboard';
import {restartAction} from '../../../DownloadManager/actions';
import {HeaderRow} from '../../../../components/table/HeaderRow';
import {Row} from '../../../../components/table/Row';
import DateCell from '../../../../components/table/cells/DateCell';
import {FsRootCell} from './FsRootCell';
import {ParametersCell} from './ParametersCell';
import {ResponseCell} from './ResponseCell';
import {PaddingCell} from './PaddingCell';
import {StatusCell} from './StatusCell';
import {HierarchyCell} from './HierarchyCell';
import {RequestorCell} from './RequestorCell';
import {RunnerCell} from './RunnerCell';
import {PauseCell} from './PauseCell';
import { ActionCell } from './ActionCell';
import { ActionsCell } from './ActionsCell';
import Paginate from '../table/Paginate';
import Filters from '../../../../components/filters/Filters';
import {
  ACTIONS_FILTERS,
  ACTION_FILTER_ACTION_ID,
  ACTION_FILTER_RUNNER_ID,
  SELECTABLE_ACTION_FILTER_NAMES,
  ACTION__SOURCE_OF_REQUEST__ACTIONSBAR,
  ACTION__SOURCE_OF_REQUEST__ACTIONSVIEW,
  ACTION_DEFAULT_PAGE_SIZE,
  ACTION_DEFAULT_SORT,
} from './constants';
import Loader from '../../../../components/Loader';
import {DISPLAY_CATEGORY_ACTION_CONTENTS} from '../../constants';
import {Collapse, Button} from 'react-bootstrap';
import ReportBugButton from "../../../../components/ReportBugButton";
import ScheduledPauseModal from '../modal/ScheduledPauseModal';

class ActionsView extends Component {

  constructor(props) {
    super(props);

    this.scrollActionID = React.createRef(null);
    this.problemDashboardRef = React.createRef(null);
    this.isAutoUpdatingRef = React.createRef(false); // Track if we're currently auto-updating to prevent loops
  }

  showDetailedViewDefault = localStorage.getItem('showActionsDetailedView') === 'true';
  showProblematicOnlyDefault = localStorage.getItem('showProblematicOnly') === 'true';

  state = {
    pageSize: ACTION_DEFAULT_PAGE_SIZE,
    currentPage: 0,
    usedFilters: (() => {
      // Check if we're on the /dashboard route - if so, force show_problematic_only to true
      const isDashboardRoute = typeof window !== 'undefined' && window.location.pathname === '/dashboard';
      
      // Check if action_id is in URL - if so, disable dashboard mode
      let hasActionIdInUrl = false;
      if (typeof window !== 'undefined' && window.location.search) {
        const urlParams = new URLSearchParams(window.location.search);
        hasActionIdInUrl = urlParams.has('action_id');
      }
      
      // If action_id is in URL, don't use dashboard mode
      const problematicOnlyValue = (isDashboardRoute && !hasActionIdInUrl) ? true : this.showProblematicOnlyDefault;
      
      let filters = {
        ...this.props.storageManager.usedActionsFilters,
        'showDetailedView': this.showDetailedViewDefault,
        'show_problematic_only': problematicOnlyValue
      };
      
      // If action_id is in URL, ONLY use action_id filter - clear all other filters
      if (hasActionIdInUrl && typeof window !== 'undefined' && window.location.search) {
        const urlParams = new URLSearchParams(window.location.search);
        const actionId = urlParams.get('action_id');
        if (actionId) {
          // Clear all filters except action_id and showDetailedView
          filters = {
            'showDetailedView': this.showDetailedViewDefault,
            'show_problematic_only': false, // Disable dashboard mode
            [ACTION_FILTER_ACTION_ID]: actionId
          };
        }
      } else {
        // Only set created_on if action_id is NOT in URL
        // If no other filters are set (regardless of checkbox state), set created_on to today
        const hasOtherFilters = Object.keys(filters).some(key => 
          key !== 'show_problematic_only' && 
          key !== 'showDetailedView' && 
          key !== 'created_on' &&
          filters[key] !== null && 
          filters[key] !== undefined && 
          filters[key] !== '' &&
          !(Array.isArray(filters[key]) && filters[key].length === 0) &&
          !(typeof filters[key] === 'object' && filters[key] !== null && Object.keys(filters[key]).length === 0)
        );
        
        if (!hasOtherFilters && (!filters.created_on || !filters.created_on.start)) {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          filters.created_on = {
            start: today,
            end: null
          };
        }
      }
      
      return filters;
    })(),
    tableSort: {
      ...ACTION_DEFAULT_SORT
    },
    showGraphForActionID: null,
    expandedActionsToDown: {},
    refreshing: false,
    isExpanded: true,
    failedActiveActions: {},
    haveUltimateRootActions: false,
    onlyShowRootActions: false,
    isUnfoldGraph: false,
    filteredActionIDs: null, // Store filtered action IDs from ProblemDashboard
    filtersDirtyFromTop: false,
  };

  componentDidMount() {
    // Store initial location search for URL change detection
    if (typeof window !== 'undefined') {
      this.prevLocationSearch = window.location.search;
    }
    
    // AUTO-LOAD: If show_problematic_only is enabled on mount, automatically load actions
    const {usedFilters} = this.state;
    if (usedFilters && usedFilters.show_problematic_only) {
      const {dispatch, history} = this.props;
      const {tableSort, pageSize} = this.state;
      const paging = {
        start: 0,
        limit: pageSize,
      };
      // Ensure created_on is set
      const filtersToUse = {...usedFilters};
      if (!filtersToUse.created_on || !filtersToUse.created_on.start) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        filtersToUse.created_on = {
          start: today,
          end: null
        };
      }
      dispatch(getActions(history, filtersToUse, ACTION__SOURCE_OF_REQUEST__ACTIONSBAR, paging, tableSort, false));
    }
  }

  componentDidUpdate(prevProps, prevState) {
    // Check if URL changed (specifically action_id parameter)
    if (typeof window !== 'undefined') {
      const currentSearch = window.location.search;
      const prevSearch = this.prevLocationSearch || '';
      
      if (currentSearch !== prevSearch) {
        this.prevLocationSearch = currentSearch;
        const urlParams = new URLSearchParams(currentSearch);
        const prevUrlParams = prevSearch ? new URLSearchParams(prevSearch) : null;
        const currentActionId = urlParams.get('action_id');
        const prevActionId = prevUrlParams ? prevUrlParams.get('action_id') : null;
        
        // If action_id is in URL, clear all other filters and only use action_id
        if (currentActionId && currentActionId !== this.state.usedFilters[ACTION_FILTER_ACTION_ID]) {
          console.log('[View] action_id in URL, clearing all other filters and using only action_id');
          // Clear all filters except action_id and showDetailedView
          const updatedFilters = {
            'showDetailedView': this.showDetailedViewDefault,
            'show_problematic_only': false, // Disable dashboard mode
            [ACTION_FILTER_ACTION_ID]: currentActionId
          };
          this.setState({ usedFilters: updatedFilters }, () => {
            // Apply filters after state update to trigger action reload
            this.handleApplyFilters();
          });
        }
      }
    }
    
    const {usedActionsFilters, actionsIsLoading} = this.props.storageManager;
    const prevUsedActionsFilters = prevProps.storageManager.usedActionsFilters;
    const {usedFilters} = this.state;
    const prevUsedFilters = prevState.usedFilters;
    
    // Normalize Date objects to strings for comparison to avoid false positives from Date serialization
    const normalizeForComparison = (filters) => {
      if (!filters) return filters;
      const normalized = {...filters};
      if (normalized.created_on) {
        normalized.created_on = {
          ...normalized.created_on,
          start: normalized.created_on.start instanceof Date 
            ? normalized.created_on.start.toISOString() 
            : (normalized.created_on.start ? String(normalized.created_on.start) : null),
          end: normalized.created_on.end instanceof Date 
            ? normalized.created_on.end.toISOString() 
            : (normalized.created_on.end ? String(normalized.created_on.end) : null)
        };
      }
      return normalized;
    };
    
    const currentNormalized = normalizeForComparison(usedActionsFilters);
    const prevNormalized = normalizeForComparison(prevUsedActionsFilters);
    const currentLocalNormalized = normalizeForComparison(usedFilters);
    const prevLocalNormalized = normalizeForComparison(prevUsedFilters);
    
    // Check if props changed (from Redux) OR local state changed (from handleChangeFilters)
    const propsChanged = JSON.stringify(currentNormalized) !== JSON.stringify(prevNormalized);
    const localStateChanged = JSON.stringify(currentLocalNormalized) !== JSON.stringify(prevLocalNormalized);
    
    if (propsChanged) {
      // Restore Date objects if they were strings
      let updatedFilters = {...usedActionsFilters};
      if (updatedFilters.created_on && updatedFilters.created_on.start && typeof updatedFilters.created_on.start === 'string') {
        updatedFilters.created_on = {
          ...updatedFilters.created_on,
          start: new Date(updatedFilters.created_on.start)
        };
      }
      
      // If no other filters are set (regardless of checkbox state), ensure created_on is set
      const hasOtherFilters = Object.keys(updatedFilters).some(key => 
        key !== 'show_problematic_only' && 
        key !== 'showDetailedView' && 
        key !== 'created_on' &&
        updatedFilters[key] !== null && 
        updatedFilters[key] !== undefined && 
        updatedFilters[key] !== '' &&
        !(Array.isArray(updatedFilters[key]) && updatedFilters[key].length === 0) &&
        !(typeof updatedFilters[key] === 'object' && updatedFilters[key] !== null && Object.keys(updatedFilters[key]).length === 0)
      );
      
      if (!hasOtherFilters && (!updatedFilters.created_on || !updatedFilters.created_on.start)) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        updatedFilters.created_on = {
          start: today,
          end: null
        };
      }
      
      this.setState({
        usedFilters: updatedFilters
      });
      
    }
    
    // AUTO-UPDATE: If show_problematic_only is enabled, automatically reload actions when filters change
    // This makes the dashboard mode more responsive - actions update automatically
    // Check local state changes (from handleChangeFilters) - this is the primary trigger
    // Use ref to prevent infinite loops and blinking
    if (
      this.state.filtersDirtyFromTop &&
      usedFilters &&
      usedFilters.show_problematic_only &&
      !actionsIsLoading &&
      !this.isAutoUpdatingRef.current
    ) {
      // Only auto-update if show_problematic_only is enabled
      // Skip if we're currently loading to avoid duplicate requests
      this.isAutoUpdatingRef.current = true; // Mark as updating to prevent re-entry
      
      const {dispatch, history} = this.props;
      const {tableSort, pageSize} = this.state;
      const paging = {
        start: 0,
        limit: pageSize,
      };
      // Ensure created_on is set if not already
      const filtersToUse = {...usedFilters};
      if (!filtersToUse.created_on || !filtersToUse.created_on.start) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        filtersToUse.created_on = {
          start: today,
          end: null
        };
      }
      const self = this;
      // Use ACTION__SOURCE_OF_REQUEST__ACTIONSBAR to indicate this is from dashboard mode
      dispatch(getActions(history, filtersToUse, ACTION__SOURCE_OF_REQUEST__ACTIONSBAR, paging, tableSort, false)).then(
        () => {
          // Use functional setState to avoid unnecessary re-renders
          self.setState(prevState => ({
            currentPage: 0,
            expandedActionsToDown: {},
            failedActiveActions: {},
            isUnfoldGraph: false,
            filtersDirtyFromTop: false
          }), () => {
            // Reset the ref after state update completes
            self.isAutoUpdatingRef.current = false;
          });
        }
      ).catch(() => {
        // Reset ref even on error
        this.isAutoUpdatingRef.current = false;
      });
    }
    if (!actionsIsLoading && prevProps.storageManager.actionsIsLoading) {
      this.setState({isExpanded: true});
    }
    
    // If show_problematic_only is enabled and actions aren't loaded yet, trigger load
    const currentUsedFilters = this.props.storageManager.usedActionsFilters;
    const currentActions = this.props.storageManager.actions;
    const currentActionsIsLoading = this.props.storageManager.actionsIsLoading;
    if (currentUsedFilters && currentUsedFilters.show_problematic_only && 
        !currentActions && !currentActionsIsLoading && 
        (!prevProps.storageManager.usedActionsFilters || !prevProps.storageManager.usedActionsFilters.show_problematic_only)) {
      // Auto-load actions when problematic filter is first enabled
      const {dispatch, history} = this.props;
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const filters = {
        show_problematic_only: true,
        created_on: currentUsedFilters.created_on || {
          start: today,
          end: null
        }
      };
      dispatch(getActions(history, filters, ACTION__SOURCE_OF_REQUEST__ACTIONSBAR, null, null, false));
    }
    if (this.props.storageManager.actions.length) {
      const haveUltimateRootActions = this.props.storageManager.actions.some(a => a.isRoot);
      if (haveUltimateRootActions !== this.state.haveUltimateRootActions) {
        this.setState({
          haveUltimateRootActions,
          onlyShowRootActions: haveUltimateRootActions,
        });
      }
    }

    if (this.scrollActionID.current) {
      for (const trEl of document.querySelectorAll("[data-action-id='" + this.scrollActionID.current + "']")) {
        setTimeout(function () {
          trEl.parentElement.classList.remove('scroll-highlight');
          trEl.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
          trEl.parentElement.classList.add('scroll-highlight');
        }, 200);
        break;
      }

      this.scrollActionID.current = null;
    }
  }

  sortAltFields = {
    createdOn: 'createdOnLong',
    performedOn: 'performedOnLong',
    expirationDate: 'expirationDateLong',
    lastActiveOn: 'lastActiveOnLong'
  };

  handleHeaderCellClick = (field, direction) => {
    const tableSort = {field, direction};
    const {usedFilters, pageSize} = this.state;
    const {dispatch, history} = this.props;
    const paging = {
      start: 0,
      limit: pageSize,
    };
    dispatch(getActions(history, usedFilters, ACTION__SOURCE_OF_REQUEST__ACTIONSVIEW, paging, tableSort, false)).then(
      () => {
        this.setState({
          currentPage: 0,
          tableSort,
          expandedActionsToDown: {},
          isUnfoldGraph: false,
        });
      }
    );
  };

  handleClickListOfContentIDs = action => {
    const {dispatch, history} = this.props;
    dispatch(getFsEntityListFromAction(history, action, true)).then(
      () => {
        this.setState({isExpanded: false});
        /*const contentTable = document.getElementsByClassName('storage-content-table')[0];
        try {
          contentTable.scrollIntoView();
        } catch (ignored) {*//**//*}*/
      }
    );
  };

  handleActionSubscriptions = action => {
    const {dispatch} = this.props;
    dispatch(showActionSubscriberView(action));
  }

  handleShowProgress = action => {
    const {dispatch} = this.props;
    dispatch(showActionProgress(action));
  };

  getWorkflowActionsByActionID = (actionID) => {
    const {actionsChildren} = this.props.storageManager;
    for (const key in actionsChildren) {
      const list = actionsChildren[key];
      if (list.find(a => a.actionID === actionID)) {
        return list;
      }
    }
    return [];
  }

  findActionInGraph = (actionID, actionsGraph) => {
    return actionsGraph.find(a => a.actionID === actionID);
  }

  isExpandedActionToDown = (action) => {
    return this.state.expandedActionsToDown[action.path] === true;
  };

  onClickToggleChildren = action => {
    const {isUnfoldGraph} = this.state;
    const isExpandedNew = !this.isExpandedActionToDown(action);
    // if unfold button was pressed and we collapse any action -> exit from unfold mode and enable unfold button
    let isUnfoldGraphNew = isUnfoldGraph;
    if (isUnfoldGraph && !isExpandedNew) {
      isUnfoldGraphNew = false;
    }
    this.setState({
      expandedActionsToDown: {...this.state.expandedActionsToDown, [action.path]: isExpandedNew},
      isUnfoldGraph: isUnfoldGraphNew
    });
  };

  getActionRequestorName = requestorId => {
    const {actionsRequestorsMap} = this.props.storageManager;
    return (actionsRequestorsMap || {})[requestorId] || requestorId;
  };

  getRunners = () => {
    const {storageManager: {lockAndServicesStatuses}} = this.props;
    const runners = {};
    if (lockAndServicesStatuses) {
      const {MEMFISService, DATService, OtherRunners} = lockAndServicesStatuses;
      const addRunner = runner => {
        if (runner && runner.RunnerID) {
          runners[runner.RunnerID] = {...runner};
        }
      };
      addRunner(MEMFISService);
      addRunner(DATService);
      Object.keys(OtherRunners || {}).forEach(k => {
        addRunner(OtherRunners[k]);
      });
    }
    return runners;
  };

  renderScheduledPauseModal = (isShow, selectedAction) => {
    const {dispatch} = this.props;
    dispatch(showScheduledPauseModal(isShow, selectedAction));
  }

  handleClickToTop = rowAction => {
    const {dispatch} = this.props;
    const {actionsChildren} = this.props.storageManager;
    const actionsGraph = actionsChildren[rowAction.actionID] || [];
    if (actionsGraph.length) {
      this.setState({showGraphForActionID: rowAction.actionID});
    } else {
      dispatch(getChildrenOfAction(rowAction.actionID)).then((res) => {
        this.setState({showGraphForActionID: rowAction.actionID});
      });
    }
  }

  findFailedActiveActions = (rowAction, actionStatus) => {
    const {dispatch} = this.props;
    const {showGraphForActionID} = this.state;

    const getChildActionsByStatus = (findStatus, actionsGraph, actionID, visitedActions) => {

      const actionInGraph = actionsGraph.find(a => a.actionID === actionID);
      const children = actionInGraph.children_new;
      const childrenActions = actionsGraph.filter(a => children.includes(a.actionID));

      let actionsByStatus = [];

      if (!childrenActions.length) {
        return actionsByStatus;
      }

      for (const child of childrenActions) {

        if (child.actionStatus === findStatus) {
          actionsByStatus.push(child);
        }

        if (visitedActions.includes(child.actionID)) {
          continue;
        }

        visitedActions.push(child.actionID);

        const tempActionsByStatus = getChildActionsByStatus(findStatus, actionsGraph, child.actionID, visitedActions);
        if (tempActionsByStatus.length) {
          actionsByStatus = [...actionsByStatus, ...tempActionsByStatus];
        }
      }

      return actionsByStatus;
    };

    const updateFailedActiveActions = (actions, actionId) => {
      const visitedActions = [];
      const failedActiveActions = getChildActionsByStatus(actionStatus, actions, actionId, visitedActions);
      this.setState(prevState => ({
        failedActiveActions: {
          ...prevState.failedActiveActions,
          [actionId]: failedActiveActions
        }
      }));
    };

    const handleWorkflowActions = (ultimateRootActionId, rowActionId) => {
      const workflowActions = this.getWorkflowActionsByActionID(ultimateRootActionId);
      if (workflowActions.length) {
        updateFailedActiveActions(workflowActions, rowActionId);
        return true;
      }
      return false;
    };

    const ultimateRootActionId = showGraphForActionID || rowAction.actionID;

    if (!handleWorkflowActions(ultimateRootActionId, rowAction.actionID)) {
      dispatch(getChildrenOfAction(ultimateRootActionId)).then((res) => {
        updateFailedActiveActions(res.actions_new, rowAction.actionID);
      });
    }
  };

  handleClickShowActiveActions = rowAction => {
    this.findFailedActiveActions(rowAction, 'ACTIVE');
  }

  handleClickShowFailedActions = rowAction => {
    this.findFailedActiveActions(rowAction, 'FAILED');
  }

  handleClickRestartAction = rowAction => {
    const {dispatch} = this.props;
    dispatch(restartAction(rowAction.actionID));
  }

  handleIgnoreProblem = (rowAction) => {
    // Reload actions to reflect the exclusion
    const {dispatch, history} = this.props;
    const {usedFilters, tableSort, pageSize, currentPage} = this.state;
    const paging = {
      start: currentPage * pageSize,
      limit: pageSize,
    };
    dispatch(getActions(history, usedFilters, ACTION__SOURCE_OF_REQUEST__ACTIONSVIEW, paging, tableSort));
  }

  handleClickGoToAction = (row, action) => {
    const {showGraphForActionID} = this.state;

    const tableActionId = showGraphForActionID || row.actionID;
    const actionsGraph = this.getWorkflowActionsByActionID(tableActionId);

    const getExpandActionRows = (findActionID, actionsGraph, actionID, parentPath = '', visitedActions) => {
      if (findActionID === actionID) {
        return [];
      }

      const actionInGraph = actionsGraph.find(a => a.actionID === actionID);
      const children = actionInGraph.children_new;
      const childrenActions = actionsGraph.filter(a => children.includes(a.actionID));

      let actionKeysForExpand = [];

      if (!childrenActions.length) {
        return actionKeysForExpand;
      }

      const currentPath = parentPath ? `${parentPath}/${actionID}` : `${actionID}`;

      const hasActionInChildren = childrenActions.find(a => a.actionID === findActionID);
      if (hasActionInChildren) {
        return [currentPath];
      }

      for (const child of childrenActions) {
        if (visitedActions.includes(child.actionID)) {
          continue;
        }

        visitedActions.push(child.actionID);

        const tempActionKeysForExpand = getExpandActionRows(findActionID, actionsGraph, child.actionID, currentPath, visitedActions);
        if (tempActionKeysForExpand.length) {
          actionKeysForExpand = [currentPath, ...tempActionKeysForExpand];
          return actionKeysForExpand;
        }
      }

      return actionKeysForExpand;
    };

    const findActionID = action.actionID;
    const currentAction = actionsGraph[0];
    const visitedActions = [];
    const actionKeysForExpand = getExpandActionRows(findActionID, actionsGraph, currentAction.actionID, '', visitedActions);

    this.scrollActionID.current = action.actionID;
    if (actionKeysForExpand.length) {
      this.setState(prevState => ({
        expandedActionsToDown: {
          ...prevState.expandedActionsToDown,
          ...actionKeysForExpand.reduce((acc, path) => ({
            ...acc,
            [path]: true
          }), {})
        },
        showGraphForActionID: tableActionId,
      }));
    } else {
      this.setState({showGraphForActionID: tableActionId});
    }
  }

  handleClickParamActionID = (row, action) => {
    const {dispatch} = this.props;
    const {showGraphForActionID} = this.state;

    const tableActionId = showGraphForActionID || row.actionID;
    const actionsGraph = this.getWorkflowActionsByActionID(tableActionId);

    if (actionsGraph.length) {
      this.handleClickGoToAction(row, action);
    } else {
      dispatch(getChildrenOfAction(tableActionId)).then((res) => {
        this.handleClickGoToAction(row, action);
      });
    }
  }

  navigateToRenderQueue = (queueID, jobID) => {
    // Only allow navigation if user is authorized
    if (!this.isAuthorizedUser()) {
      return;
    }
    // Navigate to the top-level Render Queue page with query parameters
    const { history } = this.props;
    let url = '/render_queue';
    const params = new URLSearchParams();
    if (queueID) {
      params.append('queueID', queueID);
    }
    if (jobID) {
      params.append('jobID', jobID);
    }
    if (params.toString()) {
      url += '?' + params.toString();
    }
    if (history) {
      history.push(url);
    } else {
      // Fallback if history is not available
      window.location.href = url;
    }
  };

  isAuthorizedUser = () => {
    const { user } = this.props;
    if (!user || !user.email) return false;
    const userEmail = user.email.toLowerCase();
    return userEmail.includes('berkman') || userEmail.includes('morozov');
  };

  headers = {
    toggleHierarchy: {
      title: '',
      className: 'toggle-hierarchy',
      component: HierarchyCell,
      componentArgs: {
        onClickToggleChildren: this.onClickToggleChildren,
        onClickToTop: this.handleClickToTop,
      }
    },
    actionID: {
      title: <span>Action&nbsp;ID</span>,
      className: 'action-id',
      component: ActionCell,
      componentArgs: {
        onClickBtn: this.handleActionSubscriptions
      }
    },
    actionType: {
      title: 'Type',
      className: 'action-type',
      component: PaddingCell
    },
    actionStatus: {
      title: 'Status',
      className: 'action-status',
      component: StatusCell,
      componentArgs: {
        onShowProgress: this.handleShowProgress,
        onClickStatusAction: this.handleClickGoToAction,
        onClickShowActiveActions: this.handleClickShowActiveActions,
        onClickShowFailedActions: this.handleClickShowFailedActions,
        onRestartAction: this.handleClickRestartAction,
        onIgnoreProblem: this.handleIgnoreProblem,
        onNavigateToRenderQueue: this.navigateToRenderQueue,
        user: this.props.user
      }
    },
    fsRootPath: {
      title: <span>FS&nbsp;Root</span>,
      className: 'fs-root',
      component: FsRootCell
    },
    runnerID: {
      title: 'Runner',
      className: 'action-runner',
      component: RunnerCell,
      componentArgs: {
        runners: this.getRunners()
      }
    },
    actionRequestorName: {
      title: 'Requestor',
      className: 'action-requestor',
      component: RequestorCell,
      componentArgs: {
        getRequestorName: this.getActionRequestorName
      }
    },
    actionResponse: {
      title: 'Response',
      className: 'action-response',
      component: ResponseCell
    },
    createdOn: {
      title: <span>Created&nbsp;On</span>,
      className: 'created-on',
      component: DateCell,
      componentArgs: {
        toFormat: 'M/D/YYYY hh:mm:ss A'
      }
    },
    scheduledFor: {
      title: 'Scheduled For',
      className: 'scheduled-for',
      component: DateCell,
      componentArgs: {
        toFormat: 'M/D/YYYY hh:mm:ss A'
      }
    },
    scheduledPauseTime: {
      title: 'Pause On',
      className: 'pause-on',
      component: PauseCell,
      componentArgs: {
        toFormat: 'M/D/YYYY hh:mm:ss A',
        openScheduledPauseModal: (row) => this.renderScheduledPauseModal(true, row),
      }
    },
    expirationDate: {
      title: <span>Expiration&nbsp;Date</span>,
      className: 'expiration-date',
      component: DateCell,
      componentArgs: {
        toFormat: 'M/D/YYYY hh:mm:ss A'
      }
    },
    performedOn: {
      title: <span>Performed&nbsp;On</span>,
      className: 'performed-on',
      component: DateCell,
      componentArgs: {
        toFormat: 'M/D/YYYY hh:mm:ss A'
      }
    },
    lastActiveOn: {
      title: <span>Last&nbsp;Active&nbsp;On</span>,
      className: 'last-active-on',
      component: DateCell,
      componentArgs: {
        toFormat: 'M/D/YYYY hh:mm:ss A'
      }
    },
    parameters: {
      title: 'Parameters',
      className: 'parameters',
      component: ParametersCell,
      componentArgs: {
        onClickListOfContentIDs: this.handleClickListOfContentIDs,
        onClickActionID: this.handleClickParamActionID,
      }
    }
  };

  detailedViewHeaders = {
    toggleHierarchy: {
      title: '',
      className: 'toggle-hierarchy',
      component: HierarchyCell,
      componentArgs: {
        onClickToggleChildren: this.onClickToggleChildren,
        onClickToTop: this.handleClickToTop,
      }
    },
    actionID: {
      title: <span>Action&nbsp;ID</span>,
      className: 'action-id',
      component: ActionCell,
    },
    actionType: {
      title: 'Type',
      className: 'action-type',
      component: PaddingCell
    },
    fsRootPath: {
      title: <span>Volume</span>,
      className: 'fs-root',
      component: FsRootCell
    },
    actionStatus: {
      title: 'Status',
      className: 'action-status',
      component: StatusCell,
      componentArgs: {
        onShowProgress: this.handleShowProgress,
        onClickStatusAction: this.handleClickGoToAction,
        onClickShowActiveActions: this.handleClickShowActiveActions,
        onClickShowFailedActions: this.handleClickShowFailedActions,
        onRestartAction: this.handleClickRestartAction,
        onIgnoreProblem: this.handleIgnoreProblem,
        onNavigateToRenderQueue: this.navigateToRenderQueue,
        user: this.props.user
      }
    },
    createdOn: {
      title: <span>Created&nbsp;On</span>,
      className: 'created-on',
      component: DateCell,
      componentArgs: {
        toFormat: 'M/D/YYYY hh:mm:ss A'
      }
    },
    actionRequestorName: {
      title: 'Requestor',
      className: 'action-requestor',
      component: RequestorCell,
      componentArgs: {
        getRequestorName: this.getActionRequestorName
      }
    },
    parameters: {
      title: 'Parameters',
      className: 'parameters',
      component: ParametersCell,
      componentArgs: {
        onClickListOfContentIDs: this.handleClickListOfContentIDs,
        onClickActionID: this.handleClickParamActionID,
      }
    },
    actions: {
      className: 'action-id',
      component: ActionsCell,
      componentArgs: {
        onClickBtn: this.handleActionSubscriptions
      }
    },
  };

  handleCloseView = () => {
    const {dispatch} = this.props;
    dispatch(closeActionsView());
  };

  handleChangeActionsNumber = event => {
    const {usedFilters, tableSort} = this.state;
    const {dispatch, history} = this.props;
    const pageSize = Number(event.target.value);
    const paging = {
      start: 0,
      limit: pageSize,
    };
    dispatch(getActions(history, usedFilters, ACTION__SOURCE_OF_REQUEST__ACTIONSVIEW, paging, tableSort, false)).then(
      () => {
        this.setState({
          currentPage: 0,
          pageSize,
          expandedActionsToDown: {},
          failedActiveActions: {},
          isUnfoldGraph: false,
        });
      }
    );
  };

  handlePageClick = page => {
    const {usedFilters, tableSort, pageSize} = this.state;
    const {dispatch, history} = this.props;
    const currentPage = page.selected;
    const paging = {
      start: currentPage * pageSize,
      limit: pageSize,
    };
    dispatch(getActions(history, usedFilters, ACTION__SOURCE_OF_REQUEST__ACTIONSVIEW, paging, tableSort, false)).then(
      () => {
        this.setState({
          currentPage,
          expandedActionsToDown: {},
          failedActiveActions: {},
          isUnfoldGraph: false,
        });
      }
    );
  };

  handleRefresh = () => {
    const {dispatch, history, storageManager} = this.props;
    const {usedActionsFilters} = storageManager;
    const {tableSort, pageSize, currentPage, usedFilters} = this.state;

    // If show_problematic_only is not set, remove created_on filter to prevent loops
    let filtersToUse = {...usedActionsFilters};
    if (!usedFilters.show_problematic_only && filtersToUse.created_on) {
      filtersToUse = {...filtersToUse};
      delete filtersToUse.created_on;
    }

    const paging = {
      start: currentPage * pageSize,
      limit: pageSize,
    };

    dispatch(getActions(history, filtersToUse, ACTION__SOURCE_OF_REQUEST__ACTIONSVIEW, paging, tableSort, false)).then(
      () => {
        this.setState({
          usedFilters: {...filtersToUse},
          failedActiveActions: {},
          expandedActionsToDown: {},
          isUnfoldGraph: false,
        });
      }
    );
  };

  handleApplyFilters = () => {
    const {usedFilters, tableSort, pageSize} = this.state;
    const {dispatch, history} = this.props;
    
    // Check if only show_problematic_only is set (or no other filters)
    const hasOtherFilters = Object.keys(usedFilters).some(key => 
      key !== 'show_problematic_only' && 
      key !== 'showDetailedView' && 
      key !== 'created_on' &&
      usedFilters[key] !== null && 
      usedFilters[key] !== undefined && 
      usedFilters[key] !== '' &&
      !(Array.isArray(usedFilters[key]) && usedFilters[key].length === 0) &&
      !(typeof usedFilters[key] === 'object' && usedFilters[key] !== null && Object.keys(usedFilters[key]).length === 0)
    );
    
    let finalFilters = {...usedFilters};
    
    // If no other filters are set, set Created On to today at 00:00:00 ONLY if user hasn't set it
    if (!hasOtherFilters) {
      // Only set default if created_on.start doesn't exist (user hasn't set a date)
      if (!finalFilters.created_on || !finalFilters.created_on.start) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        finalFilters = {
          ...finalFilters,
          created_on: {
            start: today,
            end: null
          }
        };
      }
    }
    
    const paging = {
      start: 0,
      limit: pageSize,
    };
    dispatch(getActions(history, finalFilters, ACTION__SOURCE_OF_REQUEST__ACTIONSVIEW, paging, tableSort, false)).then(
      () => {
        this.setState({
          currentPage: 0,
          expandedActionsToDown: {},
          failedActiveActions: {},
          isUnfoldGraph: false,
        });
      }
    );
  };

  handleResetFilters = () => {
    const {tableSort, pageSize} = this.state;
    const {dispatch, history} = this.props;
    const paging = {
      start: 0,
      limit: pageSize,
    };
    dispatch(getActions(history, {}, ACTION__SOURCE_OF_REQUEST__ACTIONSVIEW, paging, tableSort, false)).then(
      () => {
        this.setState({
          usedFilters: {},
          currentPage: 0,
          expandedActionsToDown: {},
          failedActiveActions: {},
          isUnfoldGraph: false,
          filteredActionIDs: null // Clear filtered action IDs
        });
      }
    );
  };

  handleChangeFilters = (name, value) => {
    if (SELECTABLE_ACTION_FILTER_NAMES.includes(name) && value && value.length) {
      value = value.map(item => item.value);
    } else if (name === ACTION_FILTER_ACTION_ID) {
      value = String(value).trim() === '' ? null : String(value).trim();
    }
    if (name === 'showDetailedView') {
      localStorage.setItem('showActionsDetailedView', value);
    }
    if (name === 'show_problematic_only') {
      localStorage.setItem('showProblematicOnly', value);
    
      const currentFilters = { ...this.state.usedFilters, [name]: value };
    
      const hasOtherRealFilters = Object.entries(currentFilters).some(
        ([key, val]) => {
          if (key === 'show_problematic_only') return false;
          if (key === 'created_on') {
            return val && val.start;
          }
          return val !== null && val !== undefined && val !== '';
        }
      );
    
      if (
        (!currentFilters.created_on || !currentFilters.created_on.start) &&
        !hasOtherRealFilters
      ) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        currentFilters.created_on = { start: today, end: null };
      }
    
      this.setState({
        usedFilters: currentFilters,
        filtersDirtyFromTop: true
      });
    
      return;
    }
    this.setState({usedFilters: {...this.state.usedFilters, [name]: value},
      filtersDirtyFromTop: true});
  };

  handleUnfoldAllActions = () => {
    const {showGraphForActionID} = this.state;
    const actionsGraph = this.getWorkflowActionsByActionID(showGraphForActionID);

    const getExpandActionRows = (actionsGraph, actionID, parentPath = '', visitedActions) => {
      const actionInGraph = actionsGraph.find(a => a.actionID === actionID);
      const children = actionInGraph.children_new;
      const childrenActions = actionsGraph.filter(a => children.includes(a.actionID));

      let actionKeysForExpand = [];

      if (!childrenActions.length) {
        return actionKeysForExpand;
      }

      const currentPath = parentPath ? `${parentPath}/${actionID}` : `${actionID}`;
      actionKeysForExpand.push(currentPath);

      for (const child of childrenActions) {
        if (visitedActions.includes(child.actionID)) {
          continue;
        }

        visitedActions.push(child.actionID);

        const tempActionKeysForExpand = getExpandActionRows(actionsGraph, child.actionID, currentPath, visitedActions);
        if (tempActionKeysForExpand.length) {
          actionKeysForExpand = [...actionKeysForExpand, ...tempActionKeysForExpand];
        }
      }

      return actionKeysForExpand;
    };

    const currentAction = actionsGraph[0];
    const visitedActions = [currentAction.actionID];
    const actionKeysForExpand = getExpandActionRows(actionsGraph, currentAction.actionID, '', visitedActions);

    if (actionKeysForExpand.length) {
      this.setState(prevState => ({
        expandedActionsToDown: {
          ...prevState.expandedActionsToDown,
          ...actionKeysForExpand.reduce((acc, path) => ({
            ...acc,
            [path]: true
          }), {})
        },
      }));
    }
    this.setState({
      isUnfoldGraph: true
    });
  }

  handleRefreshChildren = () => {
    const {dispatch} = this.props;
    const {showGraphForActionID, isUnfoldGraph} = this.state;
    dispatch(getChildrenOfAction(showGraphForActionID)).then(() => {
      this.setState({
        failedActiveActions: {},
      });

      if (isUnfoldGraph) {
        this.handleUnfoldAllActions();
      }
    });
  }

  renderGraphRows = (rows, emptyText, depth, absDepth, actionsGraph, tableHeaders, parentPath = '') => {
    if (!rows) {
      return null;
    }

    const {tableSort} = this.state;
    const data = depth === null ? rows : getSortedRowsBy(rows, tableSort.field, tableSort.direction, this.sortAltFields);
    return (
      <Fragment>
        {
          data.map((row, i) => {
            const rowDepth = depth === null ? row.depth : depth;
            const currentPath = parentPath ? `${parentPath}/${row.actionID}` : `${row.actionID}`;
            const isExpandedToDown = this.state.expandedActionsToDown[currentPath] === true;

            const currentAction = actionsGraph.find(a => a.actionID === row.actionID);
            const children = [];
            if (currentAction && currentAction.children_new) {
              for (const childId of currentAction.children_new) {
                const childAction = actionsGraph.find(a => a.actionID === childId);
                if (childAction) {
                  children.push(childAction);
                }
              }
            }

            return (
              <Fragment key={i}>
                <Row
                  headers={tableHeaders}
                  row={{
                    ...row,
                    depth: rowDepth,
                    absDepth,
                    isExpandedToDown,
                    useGraphMode: true,
                    actions: this.state.failedActiveActions[row.actionID] || undefined,
                    path: currentPath
                  }}
                />
                {
                  (isExpandedToDown || rowDepth < 0) ?
                    this.renderGraphRows(children, emptyText, rowDepth + 1, absDepth + 1, actionsGraph, tableHeaders, currentPath) : null
                }
              </Fragment>
            );
          })
        }
        {
          !data.length && emptyText ? (
            <tr>
              <td className="data-table-empty" colSpan={Object.keys(tableHeaders).length}>{emptyText}</td>
            </tr>
          ) : null
        }
      </Fragment>
    );
  };

  prepareFilters = () => {
    const {storageManager: {actionsFilters}, user} = this.props;
    const filters = [...actionsFilters || ACTIONS_FILTERS];
    (filters.find(f => f.name === ACTION_FILTER_RUNNER_ID) || {}).values = Object.values(this.getRunners()).map(runner => {
      const {RunnerID, MachineName, IPAddress} = runner;
      return {
        value: RunnerID,
        name: MachineName ? `${MachineName} (${RunnerID}${IPAddress ? `, ${IPAddress}` : ''})` : '(' + RunnerID + ')'
      };
    }).sort((a, b) => a.name.localeCompare(b.name));

    filters.push({
      name: 'showDetailedView',
      title: 'Show Detailed View',
      type: 'bool',
    });

    // Only show problematic filter for specific users
    const {email} = user || {};
    const enableFeatureCopyWithWoMemfis = email && true; //(email.includes('berkman') || email.includes('morozov'));
    if (enableFeatureCopyWithWoMemfis) {
      filters.push({
        name: 'show_problematic_only',
        title: 'Show Potentially Problematic Actions Only',
        type: 'bool',
      });
    }

    return filters;
  };

  renderActiveModal() {
    const {storageManager, dispatch} = this.props;
    const {isShowScheduledPauseModal, scheduledPauseSelectedAction} = storageManager;

    if (isShowScheduledPauseModal) {
      return (
        <ScheduledPauseModal
          dispatch={dispatch}
          storageManager={storageManager}
          item={scheduledPauseSelectedAction}
          onClose={() => this.renderScheduledPauseModal(false)}
          refreshFilters={this.handleRefresh}
        />
      );
    }

    return null;
  }

  renderActionsGraph = (actionID) => {
    const {storageManager} = this.props;
    const {tableSort, usedFilters} = this.state;
    const {actionsChildren} = storageManager;
    const actionsGraph = actionsChildren[actionID] || [];
    const action = actionsGraph.length ? actionsGraph[0] : null;

    // Find all actions without parents
    const findActionsWithoutParent = (allActions, excludeActionID) => {
      const allChildren = new Set();
      const allActionIds = new Set();

      // First, collect all action IDs and all children IDs
      allActions.forEach(action => {
        allActionIds.add(action.actionID);
        if (action.children_new) {
          action.children_new.forEach(childId => allChildren.add(childId));
        }
      });

      // An action is without parent if it's in allActionIds but not in allChildren
      return allActions.filter(action =>
        !allChildren.has(action.actionID) && action.actionID !== excludeActionID
      );
    };

    const actionsWithoutParent = findActionsWithoutParent(actionsGraph, actionID);

    const tableHeaders = usedFilters.showDetailedView ? this.headers : this.detailedViewHeaders;

    return (
      <>
      <div className="table-container">
        <div className="table-block">
          <table className="table">
            <HeaderRow
              headers={tableHeaders}
              sort={tableSort}
              onHeaderCellClick={this.handleHeaderCellClick}
            />
            <tbody>
              {this.renderGraphRows([action], 'No actions', 0, 0, actionsGraph, tableHeaders)}
              {actionsWithoutParent.map(act => this.renderGraphRows([act], 'No actions', 0, 0, actionsGraph, tableHeaders))}
            </tbody>
          </table>
        </div>
      </div>
      </>
    );
  }

  renderRowsForActionsTable = (rows, headers, emptyText) => {
    if (!rows) {
      return null;
    }

    const {tableSort} = this.state;
    const data = getSortedRowsBy(rows, tableSort.field, tableSort.direction, this.sortAltFields);
    return (
      <Fragment>
        {
          data.map((row, i) => {
            return (
              <Fragment key={i}>
                <Row
                  headers={headers}
                  row={{
                    ...row,
                    actions: this.state.failedActiveActions[row.actionID] || undefined,
                  }}
                />
              </Fragment>
            );
          })
        }
        {
          !data.length && emptyText ? (
            <tr>
              <td className="data-table-empty" colSpan={Object.keys(headers).length}>{emptyText}</td>
            </tr>
          ) : null
        }
      </Fragment>
    );
  };

  renderActionsContainer = (actionList, tableSort) => {
    const {showGraphForActionID} = this.state;
    if (showGraphForActionID) {
      return this.renderActionsGraph(showGraphForActionID);
    }

    return this.renderActionsTable(actionList, tableSort);
  }

  renderActionsTable = (actionList, tableSort) => {
    const {usedFilters, onlyShowRootActions, haveUltimateRootActions, currentPage, pageSize} = this.state;
    const {
      usedActionsFilters, sourceOfActionsRequest, actionsTotal
    } = this.props.storageManager;
    const pageCount = Math.ceil(actionsTotal / pageSize) || 1;
    const selectedPage = (currentPage + 1) > pageCount ? 0 : currentPage;
    const isChangedFilters = JSON.stringify(usedActionsFilters) !== JSON.stringify(usedFilters);
    // If show_problematic_only is enabled, always show actions table (don't wait for first load)
    const atFirstTime = usedFilters.show_problematic_only 
      ? false 
      : !(sourceOfActionsRequest && sourceOfActionsRequest !== ACTION__SOURCE_OF_REQUEST__ACTIONSBAR);

    const headers = usedFilters.showDetailedView ? this.headers : this.detailedViewHeaders;
    
    // Filter actions by action IDs from ProblemDashboard ONLY when we're at actions level
    // Default: show all actions (no filtering)
    let filteredActions = actionList || [];
    const {filteredActionIDs} = this.state;
    
    // Only filter if:
    // 1. ProblemDashboard ref is available
    // 2. ProblemDashboard is at 'actions' level (drilled down)
    // 3. We have filtered action IDs
    if (this.problemDashboardRef.current && filteredActionIDs && Array.isArray(filteredActionIDs) && filteredActionIDs.length > 0) {
      try {
        const dashboardState = this.problemDashboardRef.current.state;
        const isAtActionsLevel = dashboardState && dashboardState.currentLevel === 'actions';
        
        if (isAtActionsLevel) {
          // Convert to numbers for comparison (action.actionID might be a number)
          const actionIDSet = new Set(filteredActionIDs.map(id => {
            const numId = typeof id === 'string' ? parseInt(id, 10) : id;
            return isNaN(numId) ? null : numId;
          }).filter(id => id !== null && id !== undefined));
          
          if (actionIDSet.size > 0) {
            filteredActions = (actionList || []).filter(a => {
              if (!a || a.actionID == null) return false;
              const actionID = typeof a.actionID === 'string' ? parseInt(a.actionID, 10) : a.actionID;
              return !isNaN(actionID) && actionIDSet.has(actionID);
            });
          }
        }
      } catch (error) {
        // If there's any error, fall back to showing all actions
        console.error('Error filtering actions:', error);
        filteredActions = actionList || [];
      }
    }
    
    const preparedData = onlyShowRootActions ? filteredActions.filter(a => a.isRoot) : filteredActions;

    return (
      <>
      <Filters
        loading={false}
        filters={this.prepareFilters()}
        usedFilters={usedFilters}
        onChange={this.handleChangeFilters}
        applyLabel={(isChangedFilters || atFirstTime) ? 'Apply' : 'Refresh'}
        applyFilters={(isChangedFilters || atFirstTime) ? this.handleApplyFilters : this.handleRefresh}
        resetFilters={this.handleResetFilters}
        title="Filters"
        bodyMd={12}
      />
      {usedFilters.show_problematic_only && (
        <ProblemDashboard
          ref={this.problemDashboardRef}
          showProblematicOnly={usedFilters.show_problematic_only}
          dispatch={this.props.dispatch}
          onFilterChange={this.handleChangeFilters}
          createdOnFilter={usedFilters.created_on}
          onFilteredActionIDsChange={(actionIDs) => this.setState({filteredActionIDs: actionIDs})}
        />
      )}
      {!atFirstTime ?
        <>
          {haveUltimateRootActions ?
            <div className="only-root-actions">
              {onlyShowRootActions
                ? <span className="span-clickable" onClick={() => this.setState({onlyShowRootActions: false})}>Only showing root actions. Click to view all</span>
                : <span className="span-clickable" onClick={() => this.setState({onlyShowRootActions: true})}>Showing all actions. Click to view root actions only</span>
              }
            </div> : null
          }
          <div className="table-container">
            <div className="table-block">
              <table className="table">
                <HeaderRow
                  headers={headers}
                  sort={tableSort}
                  onHeaderCellClick={this.handleHeaderCellClick}
                />
                <tbody>
                  {this.renderRowsForActionsTable(preparedData, headers, 'No actions')}
                </tbody>
              </table>
            </div>
          </div>
          <Paginate
            pageCount={pageCount}
            selectedPage={selectedPage}
            onPageChange={this.handlePageClick}
            nextLabel=">"
            previousLabel="<"
            containerClassName="pagination"
          />
        </> : null
      }
      </>
    );
  };

  render() {
    const {tableSort, pageSize, refreshing, isExpanded, showGraphForActionID, isUnfoldGraph} = this.state;
    const { actions, displayCategory } = this.props.storageManager;

    return (
      <ActionTreeView>
        {this.renderActiveModal()}
        {refreshing ?
            <div>
              <Loader className="full-screen"/>
              <ReportBugButton isForModal className="loader-report-bug-btn"/>
            </div> : null}
        <div className="top-container">
          <div className="actions-number">
            {showGraphForActionID
              ? <>
                  <Button bsStyle='default' onClick={() => this.setState({showGraphForActionID: false})}>Back to actions table</Button>
                  <Button bsStyle='default' disabled={isUnfoldGraph} onClick={this.handleUnfoldAllActions}>Unfold all actions</Button>
                  <Button bsStyle='default' onClick={this.handleRefreshChildren}>Refresh</Button>
                </>
              : <>
                  <label style={{ marginLeft: '15px' }}>
                    Show
                    <select value={pageSize} onChange={this.handleChangeActionsNumber}>
                      <option value="5">5</option>
                      <option value="10">10</option>
                      <option value="25">25</option>
                      <option value="50">50</option>
                      <option value="75">75</option>
                      <option value="100">100</option>
                    </select>
                    actions
                  </label>
                </>
            }
          </div>
          <i className="fas fa-times" title="Close Actions View" onClick={this.handleCloseView}/>
        </div>
        <Collapse in={isExpanded || displayCategory !== DISPLAY_CATEGORY_ACTION_CONTENTS}>
          <div id="actions-view">
            {this.renderActionsContainer(actions, tableSort)}
          </div>
        </Collapse>
        {
          displayCategory === DISPLAY_CATEGORY_ACTION_CONTENTS ?
            <div className="collapse-action-bar">
              <span
                aria-controls="actions-view"
                aria-expanded={isExpanded}
                onClick={() => this.setState({isExpanded: !isExpanded})}
              >
              <i className="fa fa-angle-up"/>
              {`${isExpanded ? 'Collapse' : 'Expand'} Actions View`}
              </span>
            </div> : null
        }
      </ActionTreeView>
    );
  }
}


const ActionTreeView = styled.div`
  box-shadow: 0px 4px 10px rgba(0,0,0,0.05);
  padding: 15px;
  margin-bottom: 30px;
  .top-container {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: 10px;
    i {
      border-radius: 50%;
      padding: 0;
      width: 20px;
      height: 20px;
      text-align: center;
      line-height: 20px;
      &:hover {
        cursor: pointer;
        background-color: #e0e0e0;
      }
      &.fa-times {
        margin-left: 10px;
      }
    }
    .actions-number {
      select {
        margin: 0 5px;
      }
      button {
        margin-right: 10px;
      }
    }
  }
  .collapse-action-bar {
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 10px 0 15px;
    span {
      padding: 0 6px;
      border-radius: 5px;
      cursor: pointer;
      font-weight: bold;
      line-height: 20px;
      &:hover {
        background-color: #e0e0e0;
      }
      i {
        padding: 0 5px 0 0;
        height: 20px;
        font-weight: bold;
        line-height: 20px;
        transition: transform 0.2s ease 0s;
        will-change: transform;
      }
      &[aria-expanded="false"] i {
        padding: 0 0 0 5px;
        transform: rotate(180deg);
      }
    }
  }
  .table-container {
    .table-block {
      max-width: 100%;
      overflow-x: auto;
      .table {
        border-collapse: separate;
        color: #282828;
        margin-bottom: 0;
        tr {
          &:first-child {
            td {
              border-top: none;
            }
          }
          &:last-child {
            td {
              border-bottom: 1px solid #ddd;
            }
          }
          th {
            white-space: nowrap;
            cursor: pointer;
            &.filtered {
              display: flex;
              align-items: center;
            }
            &.parameters, &.toggle-hierarchy {
              pointer-events: none;
            }
          }
          td {
            &.data-table-empty {
              text-align: center;
              width: 100% !important;
            }
          }
        }

        .scroll-highlight {
          animation-name: color_change;
          animation-duration: 2s;
          animation-iteration-count: initial;
        }

        @keyframes color_change {
          from {
            background-color: white;
          }
          to {
            background-color: #ddd;
          }
        }
      }
    }
  }

  .filters-block {
    margin: 0;

    @media (min-width: 768px) {
      display: flex;
      flex-wrap: wrap;

      >.form-group {
        width: 48%;

        &:nth-of-type(even) {
          margin-left: 2%;
        }
        &:nth-of-type(odd) {
          margin-right: 2%;
        }
      }
    }

    input#action_id {
      &::-webkit-inner-spin-button, &::-webkit-outer-spin-button, &::-webkit-clear-button {
        display: none;
      }
    }

    .Select {
      .Select-menu-outer {
        max-height: 122px;
        .Select-menu {
          max-height: 120px;
        }
      }
    }
  }

  .filters-btn-toolbar {
    margin: 0 -32px;
    .btn-primary {
      min-width: 76px;
    }
  }

  .span-clickable {
    color: #007bff;
    cursor: pointer;
    &:hover {
      text-decoration: underline;
    }
`;

ActionsView.propTypes = {
  dispatch: PropTypes.func.isRequired,
  history: PropTypes.object.isRequired,
  storageManager: PropTypes.object.isRequired,
  user: PropTypes.object
}

export default ActionsView;
